// src/App.tsx
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { LeadWorkspace } from "./components/lead/LeadWorkspace";
import AuthCallback from "@/components/auth/AuthCallback";
import { FirstTimePasswordSetupGate } from "@/components/auth/FirstTimePasswordSetupGate";
import { WelcomePage } from "@/components/auth/WelcomePage";
import { useAuth } from "@/contexts/AuthContext";
import { AdminPanel } from "@/components/admin/AdminPanel";
import { GlobalSettingsControl } from "@/components/common/GlobalSettingsControl";
import { PLATFORM_DEFAULT_APP_DISPLAY_NAME } from "@/config/productDisplay";
import { finishAuthCallbackNavigation } from "@/lib/auth/finishAuthCallbackNavigation";
import { supabase } from "@/lib/supabase";
import { DriverAppShell } from "@/components/driver/DriverAppShell";
import { ServiceTechAppShell } from "@/components/service-tech/ServiceTechAppShell";
import { HaulerAppShell } from "@/components/hauler/HaulerAppShell";
import { passesDriverAppRoleGate } from "@/lib/roles/driverAccess";
import { passesServiceTechAppRoleGate } from "@/lib/service/serviceTechAccess";
import { passesHaulerPortalRoleGate } from "@/lib/roles/haulerAccess";

const WELCOME_DEV_BYPASS_KEY = "bb-welcome-dev-bypass";

/** Full-screen gates with floating settings (theme / voice) for parity with the signed-in header gear. */
function AppGateShell({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`relative min-h-screen ${className}`.trim()}>
      <div className="absolute top-4 right-4 z-30">
        <GlobalSettingsControl />
      </div>
      {children}
    </div>
  );
}

function readWelcomeDevBypass(): boolean {
  try {
    return localStorage.getItem(WELCOME_DEV_BYPASS_KEY) === "1";
  } catch {
    return false;
  }
}

export default function App() {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const [noSupabaseWelcomeBypass, setNoSupabaseWelcomeBypass] = useState(readWelcomeDevBypass);
  const auth = useAuth();

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const isPrivacyRoute = pathname === "/privacy" || pathname === "/privacy/";
  const isAuthCallback = useMemo(() => pathname === "/auth/callback", [pathname]);
  const isAdminRoute = useMemo(() => pathname === "/admin", [pathname]);
  const isDriverRoute = useMemo(() => pathname === "/driver", [pathname]);
  const isServiceTechRoute = useMemo(() => pathname === "/service-tech", [pathname]);
  const isHaulerRoute = useMemo(() => pathname === "/hauler", [pathname]);
  // Slice 1 removed the standalone impersonation banner from the chrome.
  // The Sidebar's ImpersonationPopover now provides the active indicator
  // (animated amber chip) and edit surface for the workspace.
  const withGate = (content: ReactNode) => (
    <>
      <FirstTimePasswordSetupGate />
      {content}
    </>
  );

  if (isPrivacyRoute) {
    window.location.replace("/privacy.html");
    return (
      <div className="p-6 text-sm text-gray-600 dark:text-gray-300">Opening privacy policy…</div>
    );
  }

  if (isAuthCallback) {
    return (
      <AuthCallback onDone={() => finishAuthCallbackNavigation(supabase, setPathname)} />
    );
  }

  if (isAdminRoute) {
    if (auth.status === "no-supabase")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Supabase is not configured.</div>
        </AppGateShell>
      );
    if (auth.status === "loading" || auth.status === "idle")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Loading…</div>
        </AppGateShell>
      );
    if (!auth.session)
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Sign in to continue.</div>
        </AppGateShell>
      );
    if (auth.accessStatus !== "approved")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <FirstTimePasswordSetupGate />
          <div className="p-6 pt-16">Your account is not approved.</div>
        </AppGateShell>
      );
    if (!auth.isAdmin)
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <FirstTimePasswordSetupGate />
          <div className="p-6 pt-16">Admin access required.</div>
        </AppGateShell>
      );
    return withGate(<AdminPanel />);
  }

  if (isDriverRoute) {
    if (auth.status === "no-supabase")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Supabase is not configured.</div>
        </AppGateShell>
      );
    if (auth.status === "loading" || auth.status === "idle")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Loading…</div>
        </AppGateShell>
      );
    if (!auth.session)
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Sign in to continue.</div>
        </AppGateShell>
      );
    if (auth.accessStatus !== "approved")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <FirstTimePasswordSetupGate />
          <div className="p-6 pt-16">Your account is not approved.</div>
        </AppGateShell>
      );
    if (!passesDriverAppRoleGate(auth.hasRole))
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <FirstTimePasswordSetupGate />
          <div className="p-6 pt-16">Driver access required.</div>
        </AppGateShell>
      );
    return withGate(<DriverAppShell />);
  }

  if (isServiceTechRoute) {
    if (auth.status === "no-supabase")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Supabase is not configured.</div>
        </AppGateShell>
      );
    if (auth.status === "loading" || auth.status === "idle")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Loading…</div>
        </AppGateShell>
      );
    if (!auth.session)
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Sign in to continue.</div>
        </AppGateShell>
      );
    if (auth.accessStatus !== "approved")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <FirstTimePasswordSetupGate />
          <div className="p-6 pt-16">Your account is not approved.</div>
        </AppGateShell>
      );
    if (!passesServiceTechAppRoleGate(auth.hasRole))
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <FirstTimePasswordSetupGate />
          <div className="p-6 pt-16">Service tech access required.</div>
        </AppGateShell>
      );
    return withGate(<ServiceTechAppShell />);
  }

  if (isHaulerRoute) {
    if (auth.status === "no-supabase")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Supabase is not configured.</div>
        </AppGateShell>
      );
    if (auth.status === "loading" || auth.status === "idle")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Loading…</div>
        </AppGateShell>
      );
    if (!auth.session)
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <div className="p-6 pt-16">Sign in to continue.</div>
        </AppGateShell>
      );
    if (auth.accessStatus !== "approved")
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <FirstTimePasswordSetupGate />
          <div className="p-6 pt-16">Your account is not approved.</div>
        </AppGateShell>
      );
    if (!passesHaulerPortalRoleGate(auth.hasRole))
      return (
        <AppGateShell className="bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
          <FirstTimePasswordSetupGate />
          <div className="p-6 pt-16">Hauler portal access required.</div>
        </AppGateShell>
      );
    return withGate(<HaulerAppShell />);
  }

  // No Supabase env: show welcome + explicit dev path (same as pre-auth product intent).
  if (auth.status === "no-supabase") {
    if (noSupabaseWelcomeBypass) {
      return withGate(<LeadWorkspace />);
    }
    return (
      <WelcomePage
        variant="no-supabase"
        onContinueLocalDev={() => {
          try {
            localStorage.setItem(WELCOME_DEV_BYPASS_KEY, "1");
          } catch {
            /* ignore quota / private mode */
          }
          setNoSupabaseWelcomeBypass(true);
        }}
      />
    );
  }

  // While we are loading auth/profile/membership.
  if (auth.status === "loading" || auth.status === "idle") {
    return (
      <AppGateShell className="flex min-h-screen items-center justify-center bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
        Loading your access…
      </AppGateShell>
    );
  }

  // Authenticated product: signed-out users get a welcome + sign-in, not the full workspace.
  if (!auth.session) {
    return <WelcomePage />;
  }

  if (auth.accessStatus === "pending") {
    return (
      <AppGateShell className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-6 text-gray-900 dark:from-slate-950 dark:to-slate-900 dark:text-gray-100">
        <FirstTimePasswordSetupGate />
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-400 mb-3">
          {PLATFORM_DEFAULT_APP_DISPLAY_NAME}
        </p>
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-emerald-600 dark:from-indigo-400 dark:to-emerald-400">
            Access pending approval
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Your login was created, but access is not yet approved. An administrator will review your account for the
            appropriate organization and role.
          </p>
        </div>
      </AppGateShell>
    );
  }

  if (auth.accessStatus === "blocked") {
    return (
      <AppGateShell className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-6 text-gray-900 dark:from-slate-950 dark:to-slate-900 dark:text-gray-100">
        <FirstTimePasswordSetupGate />
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400 mb-3">
          {PLATFORM_DEFAULT_APP_DISPLAY_NAME}
        </p>
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-bold text-rose-800 dark:text-rose-200">Access blocked</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This account is currently blocked from accessing the workspace. If you believe this is an error, contact an
            administrator.
          </p>
        </div>
      </AppGateShell>
    );
  }

  // Approved but no role assigned yet (e.g. auto_create_membership = false; admin will assign later).
  // Note: if membership/role is not assigned yet, we default to sales experience.

  if (auth.status === "error") {
    return (
      <AppGateShell className="flex min-h-screen items-center justify-center bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
        <FirstTimePasswordSetupGate />
        <div className="max-w-md text-center space-y-2 px-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
            {PLATFORM_DEFAULT_APP_DISPLAY_NAME}
          </p>
          <h1 className="text-2xl font-semibold">Something went wrong</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">{auth.error}</p>
        </div>
      </AppGateShell>
    );
  }

  return withGate(<LeadWorkspace />);
}